import { projectMemberRole } from "@/lib/projects/project-membership";
import {
	type ProjectMemberRole,
	resolveWhoCanAddTasks,
	type SettingsProject,
	WHO_CAN_ADD_TASKS_EVERYONE,
	WHO_CAN_ADD_TASKS_PM_ONLY,
	WHO_CAN_ADD_TASKS_PROJECT_TEAM,
	type WhoCanAddTasks,
} from "@/lib/projects/types";
import { hasPermissionByKey } from "./permission";
import type { PermissionUser } from "./types";

const rolesByWhoCanAddTasks: Partial<Record<WhoCanAddTasks, readonly ProjectMemberRole[]>> = {
	[WHO_CAN_ADD_TASKS_PM_ONLY]: ["manager"],
	[WHO_CAN_ADD_TASKS_PROJECT_TEAM]: ["manager", "member"],
};

/**
 * Angular-parity gate for creating tasks on a project (`userProjectTaskPerm`).
 */
export const hasCreateTaskPermission = (
	user: PermissionUser | null | undefined,
	project: SettingsProject | null | undefined
): boolean => {
	if (!(user && project) || project.useAsDefault) {
		return false;
	}

	const whoCanAddTasks = resolveWhoCanAddTasks(project.private, project.whoCanAddTasks);

	if (whoCanAddTasks === WHO_CAN_ADD_TASKS_EVERYONE) {
		return true;
	}

	const isWorkspaceAdmin = !!user.workspace?.users?.find(
		(workspaceUser) => workspaceUser.id === user.id && workspaceUser.admin === true
	);
	if (isWorkspaceAdmin) {
		return true;
	}

	if (hasPermissionByKey(user, "groupsCanManageAllProjects") || hasPermissionByKey(user, "groupsCanManageTasks")) {
		return true;
	}

	const allowedRoles = rolesByWhoCanAddTasks[whoCanAddTasks];
	if (!allowedRoles) {
		return false;
	}

	const userGroupIds = (user.workspace?.groups ?? [])
		.filter((group) => group.users?.some((member) => member.id === user.id))
		.map((group) => group.id);
	const role = projectMemberRole(user.id, project.users, project.groups, userGroupIds);
	return !!role && allowedRoles.includes(role);
};
